/* ============================================================
   Atari Club Profile Survey — client script
   ------------------------------------------------------------
   Responsibilities:
   - Step navigation between the 3 missions (+ success state)
   - Per-mission required-field validation
   - localStorage auto-save/restore (in case of tab close)
   - Serialize all inputs into JSON payload
   - POST to the Cloudflare worker with customer_id + token
   - On success: show reward code screen + clear saved state
   ============================================================ */

(function () {
  'use strict';

  var form = document.getElementById('AtariClubProfileForm');
  var closedPanel = document.querySelector('[data-atari-survey-closed]');
  var loginCard = document.querySelector('[data-atari-survey-login]');
  var wrap = document.querySelector('.atari-survey-wrap');

  // workerBase is on the form when logged in, otherwise on the wrap (so the
  // logged-out login card can also trigger the closed-state swap).
  var workerBase =
    (form && form.getAttribute('data-worker-base')) ||
    (wrap && wrap.getAttribute('data-worker-base')) || '';

  // ---- Survey-closed gate --------------------------------
  // Hit the worker's /open endpoint on page load. If the 1000-response
  // cap has been reached, hide whatever card is currently showing
  // (form, login CTA, or thank-you) and reveal the closed panel instead.
  // Runs for BOTH logged-in and logged-out visitors.
  function showClosedState() {
    if (closedPanel) closedPanel.hidden = false;
    var formCard = form && form.closest('.atari-survey-card');
    if (formCard) formCard.style.display = 'none';
    if (loginCard) loginCard.style.display = 'none';
  }
  (function checkSurveyOpen() {
    if (!workerBase) return;
    fetch(workerBase + '/open', { method: 'GET' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (data && data.open === false) showClosedState();
      })
      .catch(function () { /* fail open — don't block the form if /open errors */ });
  })();

  // Everything below this point is form-specific. Logged-out visitors
  // don't have the form, but the /open check above still ran for them.
  if (!form) return;
  var customerId = form.getAttribute('data-customer-id');
  var customerEmail = form.getAttribute('data-customer-email');
  var rawToken = form.getAttribute('data-token') || '';

  var LS_KEY = 'atari:profile:draft:' + customerId;
  var START_KEY = 'atari:profile:started:' + customerId;
  var missions = form.querySelectorAll('[data-mission]');
  var stepsContainer = document.querySelector('[data-steps]');
  var stepEls = document.querySelectorAll('.atari-survey__step[data-step]');
  var errorBox = form.querySelector('[data-error-box]');
  var codeCard = form.querySelector('[data-code-card]');
  var codeEl = form.querySelector('[data-code]');
  var copyBtn = form.querySelector('[data-copy-code]');

  var currentStep = 1;
  var submitting = false;

  // ---- Timing: record when user first saw the form ---------
  var startedAt = null;
  try {
    var saved = localStorage.getItem(START_KEY);
    if (saved && !isNaN(parseInt(saved, 10))) {
      startedAt = parseInt(saved, 10);
    } else {
      startedAt = Date.now();
      localStorage.setItem(START_KEY, String(startedAt));
    }
  } catch (e) { startedAt = Date.now(); }

  // ---- Step navigation -------------------------------------
  /**
   * @param {number|'success'} n
   * @param {{scroll?: boolean}} [opts]  default scroll=true; pass scroll:false
   *   on the initial render so the browser stays at the page's actual top
   *   (title + intro visible). Next/Back buttons scroll to the mission
   *   heading as expected.
   */
  function showStep(n, opts) {
    opts = opts || {};
    var shouldScroll = opts.scroll !== false;

    missions.forEach(function (m) {
      var name = m.getAttribute('data-mission');
      m.classList.toggle(
        'is-active',
        name === String(n) || (n === 'success' && name === 'success')
      );
    });
    currentStep = n;
    updateProgress(n);

    // Hide the intro header (title, lede, limit note, privacy text) +
    // step indicator when the user lands on the success state — they've
    // already submitted, so the marketing copy isn't needed.
    var card = form.closest('.atari-survey-card');
    if (card) card.classList.toggle('is-success-state', n === 'success');

    if (!shouldScroll) return;

    // Scroll the current mission's heading into view so the user sees
    // "Mission N of 3" immediately. CSS scroll-margin-top leaves room
    // so it isn't glued to the top of the viewport.
    var activeMission = form.querySelector('[data-mission="' + (n === 'success' ? 'success' : n) + '"]');
    var scrollTarget = activeMission && (activeMission.querySelector('.atari-survey-mission__heading') || activeMission);
    if (scrollTarget) {
      try {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {}
    }
  }

  /**
   * Update the visual step indicator:
   * - Completed steps (< current) get `is-completed` (checkmark)
   * - The current step gets `is-active` (highlighted circle)
   * - Future steps have neither class
   * - The fill-bar behind the circles (--fill) goes 0 → 50 → 100 as
   *   steps are completed, reaching each circle's position.
   */
  function updateProgress(n) {
    var numeric = (n === 'success') ? 4 : n;
    var fill = 0;
    if (numeric === 2) fill = 50;
    else if (numeric === 3) fill = 100;
    else if (numeric === 4) fill = 100; // success — everything done

    if (stepsContainer) stepsContainer.style.setProperty('--fill', fill + '%');

    stepEls.forEach(function (el) {
      var step = parseInt(el.getAttribute('data-step'), 10);
      el.classList.toggle('is-completed', step < numeric);
      el.classList.toggle('is-active', step === numeric);
    });
  }

  // ---- Validation per step ---------------------------------
  /**
   * Every `.atari-survey-q` that contains a `.required` marker is a required
   * question. This covers <fieldset>/<legend> questions AND Likert matrices
   * (which use a div.atari-survey-likert__statement instead of a legend).
   *
   * Within each required question, we group inputs by `name` and check that
   * every group has at least one value. Likert matrices have multiple names
   * (one per row, e.g. brand_perception[modern_brand]), so this catches the
   * case where a user filled SOME rows but not others.
   */
  function validateStep(step) {
    clearError();
    var section = form.querySelector('[data-mission="' + step + '"]');
    if (!section) return true;

    var questions = section.querySelectorAll('.atari-survey-q');
    var firstInvalid = null;
    var invalidCount = 0;

    questions.forEach(function (fs) {
      fs.classList.remove('is-invalid');

      // Required only if the question has a .required marker somewhere.
      var isRequired = !!fs.querySelector('.required');
      if (!isRequired) return;

      // Collect inputs (skip honeypot), group by `name`.
      var inputs = fs.querySelectorAll('input:not([name="honeypot"]), textarea, select');
      var groups = {};
      inputs.forEach(function (i) {
        var nm = i.name;
        if (!nm) return;
        if (!groups[nm]) groups[nm] = [];
        groups[nm].push(i);
      });
      if (Object.keys(groups).length === 0) return;

      // Every name-group must have at least one filled input.
      var allGroupsFilled = true;
      Object.keys(groups).forEach(function (nm) {
        var group = groups[nm];
        var filled = false;
        group.forEach(function (i) {
          if (i.type === 'radio' || i.type === 'checkbox') {
            if (i.checked) filled = true;
          } else {
            if (i.value && String(i.value).trim()) filled = true;
          }
        });
        if (!filled) allGroupsFilled = false;
      });

      if (!allGroupsFilled) {
        fs.classList.add('is-invalid');
        invalidCount++;
        if (!firstInvalid) firstInvalid = fs;
      }
    });

    if (firstInvalid) {
      var msg = invalidCount === 1
        ? 'Please answer the highlighted question before continuing.'
        : 'Please answer the ' + invalidCount + ' highlighted questions before continuing.';
      showError(msg);
      try { firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      return false;
    }
    return true;
  }

  // ---- Serialize ------------------------------------------
  function serializeForm() {
    var data = {};
    var fd = new FormData(form);

    // Handle array fields (checkboxes) and Likert bracket syntax.
    // ANY question rendered as `<input type="checkbox" name="X">` (where the
    // user can pick multiple) MUST be listed here, otherwise FormData
    // collapses multi-select to the last value, which is then sent as a
    // string instead of an array and breaks server-side list-metafield writes.
    var arrayKeys = new Set([
      'platforms',
      'genres',
      'discovery_channels',
      'social_follows',
      'follows_game_companies',
    ]);

    for (var pair of fd.entries()) {
      var key = pair[0], val = pair[1];

      // Likert: name like "brand_perception[modern_brand]"
      var m = key.match(/^(\w+)\[(\w+)\]$/);
      if (m) {
        if (!data[m[1]]) data[m[1]] = {};
        var num = parseInt(val, 10);
        data[m[1]][m[2]] = isNaN(num) ? val : num;
        continue;
      }

      if (arrayKeys.has(key)) {
        if (!data[key]) data[key] = [];
        data[key].push(val);
        continue;
      }

      if (key === 'nps_score') {
        var n = parseInt(val, 10);
        data[key] = isNaN(n) ? null : n;
        continue;
      }

      data[key] = val;
    }

    return data;
  }

  // ---- LocalStorage save/restore ---------------------------
  function saveDraft() {
    try {
      var d = serializeForm();
      localStorage.setItem(LS_KEY, JSON.stringify({ at: Date.now(), step: currentStep, data: d }));
    } catch (e) {}
  }
  /**
   * Returns the mission step to resume at (1–3), or 1 if no valid draft.
   * Restores both answers AND the user's last position so a token-expiry
   * reload doesn't bump them back to Mission 1 after they've filled
   * everything out.
   */
  function restoreDraft() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return 1;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.data) return 1;
      // Only restore if saved within the last 24 hours
      if (Date.now() - parsed.at > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(LS_KEY);
        return 1;
      }
      restoreDataIntoForm(parsed.data);
      var savedStep = parseInt(parsed.step, 10);
      if (savedStep >= 1 && savedStep <= 3) return savedStep;
      return 1;
    } catch (e) { return 1; }
  }
  function restoreDataIntoForm(d) {
    Object.keys(d).forEach(function (k) {
      var v = d[k];
      if (Array.isArray(v)) {
        v.forEach(function (val) {
          var el = form.querySelector('[name="' + k + '"][value="' + CSS.escape(String(val)) + '"]');
          if (el) el.checked = true;
        });
      } else if (v && typeof v === 'object') {
        // Likert
        Object.keys(v).forEach(function (subK) {
          var el = form.querySelector('[name="' + k + '[' + subK + ']"][value="' + v[subK] + '"]');
          if (el) el.checked = true;
        });
      } else if (v !== null && v !== undefined) {
        var els = form.querySelectorAll('[name="' + k + '"]');
        els.forEach(function (el) {
          if (el.type === 'radio' || el.type === 'checkbox') {
            if (String(el.value) === String(v)) el.checked = true;
          } else {
            el.value = v;
          }
        });
      }
    });
  }
  function clearDraft() {
    try {
      localStorage.removeItem(LS_KEY);
      localStorage.removeItem(START_KEY);
    } catch (e) {}
  }

  // Auto-save on any input change, throttled.
  var saveTimer = null;
  form.addEventListener('input', function () {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveDraft, 500);
  });
  form.addEventListener('change', function () {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveDraft, 250);
  });

  // ---- Telemetry: report submit failures to the worker ----
  /**
   * Fire-and-forget POST to /errors so we capture every failed submit
   * with technical context. `keepalive: true` lets the request survive
   * page navigations after the failed submit. Never throws / blocks UI.
   */
  function reportSubmitError(detail) {
    if (!workerBase) return;
    try {
      var payload = {
        customer_id: customerId,
        email: customerEmail,
        kind: detail.kind || 'unknown',
        step: currentStep,
        duration_seconds: Math.round((Date.now() - (startedAt || Date.now())) / 1000),
        response_status: detail.status != null ? detail.status : null,
        response_body: detail.body ? JSON.stringify(detail.body).slice(0, 500) : null,
        message: detail.message || null,
        viewport: window.innerWidth + 'x' + window.innerHeight,
        page_url: location.pathname,
      };
      fetch(workerBase + '/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () {});
    } catch (e) {}
  }

  // ---- Error surface ---------------------------------------
  function showError(msg) {
    if (!errorBox) return;
    errorBox.textContent = msg;
    errorBox.hidden = false;
  }
  function clearError() {
    if (!errorBox) return;
    errorBox.textContent = '';
    errorBox.hidden = true;
  }

  // ---- Next / Prev buttons ---------------------------------
  form.querySelectorAll('[data-next]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      if (!validateStep(currentStep)) return;
      showStep(currentStep + 1);
    });
  });
  form.querySelectorAll('[data-prev]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      clearError();
      if (typeof currentStep === 'number') showStep(currentStep - 1);
    });
  });

  // ---- Block Enter-as-submit on non-final steps ------------
  // Browsers natively submit a <form> when the user presses Enter in a
  // single-line text input. Without this guard, hitting Enter while filling
  // favorite_game (Mission 1) submits the entire form mid-flow — Mission 2 +
  // 3 fields are empty, the worker accepts the payload, and it lands as
  // speed_trap + attention_failed in QA. Allow Enter in textareas (line
  // breaks) and only on the actual submit step.
  form.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    if (e.target && e.target.tagName === 'TEXTAREA') return;
    if (currentStep !== 3) e.preventDefault();
  });

  // ---- Submit ----------------------------------------------
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (submitting) return;
    // Hard guard: submission is only valid from Mission 3. Any earlier-step
    // submission (Enter key, programmatic, DOM tampering) is rejected.
    if (currentStep !== 3) return;
    // Validate EVERY mission, not just the current one — protects against
    // the user navigating Back, clearing a value, and re-advancing.
    for (var s = 1; s <= 3; s++) {
      if (!validateStep(s)) {
        if (s !== currentStep) showStep(s);
        return;
      }
    }

    var answers = serializeForm();
    var token = rawToken;

    var duration = Math.round((Date.now() - (startedAt || Date.now())) / 1000);
    var payload = {
      token: token,
      answers: answers,
      timing: {
        duration_seconds: duration,
        started_at: startedAt,
        submitted_at: Date.now(),
      },
    };

    // Flush a sync draft save before going async — guarantees the latest
    // answers are in localStorage before any error path (network failure,
    // token_expired auto-reload, etc.) could lose them.
    try { saveDraft(); } catch (e) {}

    submitting = true;
    var submitBtn = form.querySelector('.atari-survey__btn--submit');
    var originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
    }
    clearError();

    fetch(workerBase + '/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(function (res) { return res.json().then(function (data) { return { res: res, data: data }; }); })
      .then(function (out) {
        submitting = false;
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }

        if (out.res.ok && out.data && out.data.ok) {
          if (codeCard && codeEl && out.data.discount_code) {
            codeEl.textContent = out.data.discount_code;
            codeCard.hidden = false;
          }
          clearDraft();
          showStep('success');
          return;
        }

        if (out.data && out.data.error === 'already_completed') {
          if (codeCard && codeEl && out.data.discount_code) {
            codeEl.textContent = out.data.discount_code;
            codeCard.hidden = false;
          }
          clearDraft();
          showStep('success');
          return;
        }

        if (out.data && out.data.error === 'rate_limited') {
          // Expected — user double-clicked / retried within 60s. Don't log.
          showError('You just submitted — please wait a moment and try again.');
          return;
        }

        if (out.data && out.data.error === 'survey_closed') {
          showClosedState();
          return;
        }

        if (out.data && out.data.error === 'invalid_token') {
          reportSubmitError({ kind: 'invalid_token', status: out.res.status, body: out.data });
          showError('Your session expired. Please refresh the page and try again.');
          return;
        }

        // Token expired (user sat on the page >2 hours). Their answers are
        // already in localStorage from the auto-save handler — flush a final
        // sync save just in case, set a resume flag for the post-reload
        // toast, and reload the page so Liquid mints a fresh token. After
        // reload, restoreDraft() rehydrates the form AND jumps them back to
        // the mission they were on.
        if (out.data && out.data.error === 'token_expired') {
          try { saveDraft(); } catch (e) {}
          try { sessionStorage.setItem('atari:profile:resumed', '1'); } catch (e) {}
          reportSubmitError({ kind: 'token_expired', status: out.res.status, body: out.data });
          showError('Your session expired — refreshing to restore your progress…');
          setTimeout(function () { try { location.reload(); } catch (e) {} }, 1500);
          return;
        }

        reportSubmitError({
          kind: (out.data && out.data.error) || 'unknown_submit_failure',
          status: out.res.status,
          body: out.data,
          message: 'Submit returned non-OK',
        });
        showError(
          'Something went wrong submitting your survey. Please try again or contact support if it keeps happening.'
        );
        console.warn('Survey submit failed', out);
      })
      .catch(function (err) {
        submitting = false;
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }
        reportSubmitError({
          kind: 'network_error',
          message: String(err && err.message || err),
        });
        showError('Network error. Please check your connection and try again.');
        console.error(err);
      });
  });

  // ---- Copy code button ------------------------------------
  if (copyBtn && codeEl) {
    copyBtn.addEventListener('click', function () {
      var text = codeEl.textContent || '';
      if (!text) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function () {
          var original = copyBtn.textContent;
          copyBtn.textContent = 'Copied!';
          setTimeout(function () { copyBtn.textContent = original; }, 2000);
        });
      }
    });
  }

  // ---- Init ------------------------------------------------
  // restoreDraft repopulates fields AND returns the mission to resume at.
  // If the user just came back from a token_expired auto-reload, this puts
  // them right back where they were instead of dumping them at Mission 1.
  var initialStep = restoreDraft() || 1;
  // Initial render — DON'T scroll; user should land at page top with the
  // title + intro visible. Subsequent Next/Back clicks scroll to heading.
  showStep(initialStep, { scroll: false });

  // Surface a brief confirmation if we just came back from a token_expired
  // auto-reload, so the user understands "your progress is restored, please
  // submit again" rather than wondering why nothing happened.
  try {
    if (sessionStorage.getItem('atari:profile:resumed') === '1') {
      sessionStorage.removeItem('atari:profile:resumed');
      showError('Your session refreshed and your answers were restored. Please review and submit again.');
    }
  } catch (e) {}
})();
